import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Input from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import AddRTOCityLocation from "./AddRTOCityLocation";
import AddZoneDialog from "./AddZoneDialog";
import ConfirmBox from "../../../UiComponents/ConfirmBox/ConfirmBox";
import {
    AddIcon,
    SearchIcon,
    DeleteIcon,
} from "../../../Resources/Icons/icons";
import {
    GetRtoCityLocation,
    DeleteRtoCityLocation,
    UpdateRtoCityLocation,
    FilterRtoCityLocation,
    GetUserFindbyId,
    GetZone,
} from "../../../Service/_index";
import moment from "moment";
import Dialog from "@mui/material/Dialog";
import { Transition } from "../../../UiComponents/Transition/Transition";
import { Divider } from "@mui/material";
import TabFilter from "../../../Components/Tabs/Tabs";
import MasterCommonTable from "../../../UiComponents/CommonTable/MasterCommonTable";
import DeleteImage from "../../../../src/Resources/Images/delete_common.png";

const FilterOption = [
    {
        label: "All Data",
        isActive: "all",
        value: 1,
    },
    {
        label: "Active Data",
        isActive: "true",
        value: 2,
    },
    {
        label: "Inactive Data",
        isActive: "false",
        value: 3,
    },
];

const getClassName = (data) => {
    return data.status === "Active"
        ? "Active-chip"
        : data.status === "Inactive"
            ? " inActive-chip"
            : "";
};

const headCells = [
    {
        id: "RTOCode",
        placeMent: true,
        disablePadding: false,
        label: "RTO Code",
    },
    {
        id: "city",
        placeMent: true,
        disablePadding: false,
        label: "City",
    },
    {
        id: "Region",
        placeMent: true,
        disablePadding: false,
        label: "Region",
    },
    {
        id: "zoneLabel",
        placeMent: true,
        disablePadding: false,
        label: "Zone",
    },
    {
        id: "createdAt",
        placeMent: true,
        disablePadding: false,
        label: "Created Date",
    },
    {
        id: "status",
        placeMent: true,
        disablePadding: false,
        label: "Status",
        class: getClassName,
    },
];

const RTOCityLocation = () => {
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [selectedData, setSelectedData] = React.useState({});
    const [openDrawer, setOpenDrawer] = React.useState(false);
    const [openDeleteConfirm, setOpenDeleteConfirm] = React.useState(false);
    const [data, setData] = React.useState([]);
    const [searchText, setSearchText] = useState("");
    const [selectedTab, setSelectedTab] = useState(1);
    const [loading, setLoading] = useState(false);
    const [openZoneDialog, setOpenZoneDialog] = useState(false);
    // Zones are fetched alongside the rows and joined by id, so the table shows
    // the zone's NAME and rate rather than a raw ObjectId.
    const [zoneList, setZoneList] = useState([]);

    // ── Assigned-only view for non-admin users ──────────────────────────────
    // A CLIENT (admin) manages the shared master. Every other role sees ONLY
    // the RTO cities assigned to them (User.rtocityids) and may add new ones,
    // but never edit or deactivate a master row — that row is shared with
    // every other user.
    const loggedInUserType = sessionStorage.getItem("userType");
    const isMasterAdmin =
        String(loggedInUserType || "").toUpperCase() === "CLIENT";
    const [userRtoRows, setUserRtoRows] = React.useState([]);

    const tableConfig = {
        isAction: isMasterAdmin,
        isEdit: isMasterAdmin,
        // A switch, NOT a delete button. Delete only soft-deletes
        // (isActive:false) yet the button stayed on the row afterwards, so an
        // already-deleted record could be "deleted" over and over with no way
        // to bring it back. The switch shows the real state and toggles both
        // ways.
        isDelete: false,
        isSwitch: isMasterAdmin,
        isSearch: true,
        historyEntity: "rtoCityLocation",
    };

    const loadUserAssignments = React.useCallback(() => {
        const userId = sessionStorage.getItem("UserId");
        if (isMasterAdmin || !userId) {
            setUserRtoRows([]);
            return Promise.resolve([]);
        }
        return GetUserFindbyId(userId)
            .then((res) => {
                const rows = res?.data?.rtocityids || [];
                setUserRtoRows(rows);
                return rows;
            })
            .catch(() => {
                setUserRtoRows([]);
                return [];
            });
    }, [isMasterAdmin]);

    /**
     * Keep only the rows assigned to this user.
     *
     * Unlike Make & Model there is NO "no assignments = show everything"
     * fallback: an unassigned user is meant to see an empty list and add their
     * own, so the master is never exposed wholesale.
     */
    const restrictToAssigned = React.useCallback(
        (rows, assignments) => {
            if (isMasterAdmin) return rows;
            const assignedIds = new Set(
                (assignments || []).map((r) => String(r.rtoCity))
            );
            return rows.filter((item) => assignedIds.has(String(item._id)));
        },
        [isMasterAdmin]
    );

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
        const selectedOption = FilterOption.find(
            (option) => option.value === newValue
        );
        FilterFunction(selectedOption);
    };

    const FilteredData = data.filter((el) => {
        return `${el?.RTOCode},${el?.Region || ""},${el?.city}`
            .toLowerCase()
            .includes(searchText.toLowerCase());
    });

    /**
     * The zone's NAME for the table — no rate.
     *
     * The rate belongs on the Zone master and in the picker where it informs
     * the choice; repeating it on every row here just adds noise.
     */
    const zoneLabelFor = (zoneId, zones) => {
        if (!zoneId) return "--";
        const zone = (zones || []).find((z) => String(z._id) === String(zoneId));
        return zone ? zone.zoneName : "--";
    };

    const GetData = () => {
        setLoading(true);
        Promise.all([GetRtoCityLocation(), loadUserAssignments(), GetZone()])
            .then(([res, assignments, zoneRes]) => {
                const zones = Array.isArray(zoneRes?.data) ? zoneRes.data : [];
                setZoneList(zones);
                if (res && res.data) {
                    const updatedData = res.data.map((item) => ({
                        ...item,
                        zoneLabel: zoneLabelFor(item.zoneId, zones),
                        createdAt: moment(item.createdAt).format("DD/MM/YYYY hh:mm A"),
                        status: item.isActive ? "Active" : "Inactive",
                        // MasterCommonTable's switch reads isEnabled; this collection
                        // stores isActive, so map it across or the switch reads as
                        // off for every row.
                        isEnabled: !!item.isActive,
                    }));
                    setData(restrictToAssigned(updatedData, assignments));
                }
            })
            .finally(() => setLoading(false));
    };

    React.useEffect(() => {
        GetData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEdit = (row) => {
        setSelectedData(row);
        setOpenDrawer(true);
    };

    const handleAdd = () => {
        setSelectedData({});
        setOpenDrawer(true);
    };

    const handleDelete = (row) => {
        setSelectedData(row);
        setOpenDeleteConfirm(true);
    };

    const confirmDelete = () => {
        DeleteRtoCityLocation(selectedData._id).then((res) => {
            refreshCurrentView();
            setOpenDeleteConfirm(false);
        });
    };

    /**
     * Toggle a row between active and inactive.
     *
     * Deactivating goes through DeleteRtoCityLocation (which sets
     * isActive:false); reactivating writes isActive back to true. The switch is
     * what makes an already-deactivated row recoverable — the old delete button
     * had no counterpart, so a deleted row could only be deleted again.
     */
    const handleToggleActive = (row, currentlyEnabled) => {
        const request = currentlyEnabled
            ? DeleteRtoCityLocation(row._id)
            : UpdateRtoCityLocation(row._id, { isActive: true });

        request
            .then(() => refreshCurrentView())
            .catch((error) => console.log(error));
    };

    const FilterFunction = (e) => {
        let isActive = e?.isActive;
        if (isActive === "all") {
            GetData();
        } else {
            setLoading(true);
            Promise.all([
                FilterRtoCityLocation({ isActive: isActive === "true" }),
                loadUserAssignments(),
                GetZone(),
            ])
                .then(([res, assignments, zoneRes]) => {
                    const zones = Array.isArray(zoneRes?.data) ? zoneRes.data : zoneList;
                    if (res && res.data) {
                        const updatedData = res.data.map((item) => ({
                            ...item,
                            zoneLabel: zoneLabelFor(item.zoneId, zones),
                            createdAt: moment(item.createdAt).format("DD/MM/YYYY hh:mm A"),
                            status: item.isActive ? "Active" : "Inactive",
                            // MasterCommonTable's switch reads isEnabled; this collection
                            // stores isActive, so map it across or the switch reads as
                            // off for every row.
                            isEnabled: !!item.isActive,
                        }));
                        // The Active/Inactive tab narrows the master; the
                        // assigned filter still applies on top of it.
                        setData(restrictToAssigned(updatedData, assignments));
                    }
                })
                .finally(() => setLoading(false));
        }
        setPage(0);
    };

    /**
     * Reload while STAYING on the selected tab.
     *
     * Every action used to call GetData(), which fetches the whole master —
     * so after toggling a row on the "Active Data" tab the table filled with
     * every record while the tab still read Active. Route refreshes through
     * here so the view always matches the tab.
     */
    const refreshCurrentView = () => {
        const option = FilterOption.find((o) => o.value === selectedTab);
        if (!option || option.isActive === "all") {
            GetData();
        } else {
            FilterFunction(option);
        }
    };

    return (
        <>
            <Grid container className="Master_Card_Container">
                <Grid
                    container
                    alignItems={"center"}
                    className="Master_Card_Sub_Container"
                >
                    <Grid item xs={12} sm={6} md={6}>
                        <Typography className="Master_Card_Tittle">RTO City Location</Typography>
                        {!isMasterAdmin ? (
                            <Typography sx={{ fontSize: "12px", color: "#667085", mt: 0.5 }}>
                                Showing the RTO cities assigned to you. Anything you
                                create here is added to your list automatically.
                            </Typography>
                        ) : null}
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={6}
                        className="d-flex justify-content-end"
                    >
                        <Button
                            className="Master_Card_create_Button"
                            startIcon={<AddIcon />}
                            onClick={() => setOpenZoneDialog(true)}
                            sx={{ mr: 1.5, whiteSpace: "nowrap" }}
                        >
                            Zone
                        </Button>
                        <Button
                            className="Master_Card_create_Button"
                            startIcon={<AddIcon />}
                            onClick={handleAdd}
                            sx={{ whiteSpace: "nowrap" }}
                        >
                            Create
                        </Button>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Divider sx={{ backgroundColor: "#EAECF0", borderWidth: 1 }} />
                </Grid>
                <Grid
                    container
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    className="Master_Card_Sub_Container"
                >
                    <TabFilter
                        options={FilterOption}
                        selectedTab={selectedTab}
                        onTabChange={handleTabChange}
                    />
                    <Grid item xs={12} sm={4} md={3.1} mt={{ xs: 2, sm: 0, md: 0 }}>
                        <Input
                            className="w-100 Master_Card_Input"
                            onChange={(e) => {
                                setSearchText(e.target.value);
                                setPage(0);
                            }}
                            disableUnderline={true}
                            placeholder="Search in RTO City..."
                            startAdornment={
                                <InputAdornment position="start">
                                    <IconButton>
                                        <SearchIcon sx={{ fontSize: "20px" }} />
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                    </Grid>
                </Grid>
                <MasterCommonTable
                    tableConfig={tableConfig}
                    data={FilteredData}
                    columns={headCells}
                    handleEdit={handleEdit}
                    handleDelete={handleDelete}
                    handleApprove={handleToggleActive}
                    tableTotalCount={FilteredData.length}
                    loading={loading}
                />
            </Grid>

            <Dialog
                open={openDrawer}
                TransitionComponent={Transition}
                keepMounted
                PaperProps={{
                    sx: {
                        maxWidth: { xs: "80%", sm: "70%", md: "50%", lg: "40%" },
                    },
                }}
            >
                {openDrawer && (
                    <AddRTOCityLocation
                        title={selectedData._id ? "Edit" : "Create"}
                        selectedData={selectedData}
                        formType={selectedData._id ? "edit" : "add"}
                        setOpenDrawer={setOpenDrawer}
                        GetData={refreshCurrentView}
                        // Non-admins add for themselves: the new (or matched)
                        // master row is appended to their own rtocityids.
                        mapToUser={!isMasterAdmin}
                        userRtoRows={userRtoRows}
                    />
                )}
            </Dialog>

            <Dialog
                open={openZoneDialog}
                onClose={() => setOpenZoneDialog(false)}
                TransitionComponent={Transition}
                keepMounted
                // index.scss forces .MuiDialog-paper to width:100% !important,
                // so the size has to be capped through maxWidth, not width.
                PaperProps={{ sx: { maxWidth: "560px !important", overflow: "visible" } }}
            >
                {openZoneDialog && (
                    <AddZoneDialog
                        onClose={() => setOpenZoneDialog(false)}
                        // Refresh the list so an RTO showing a deleted zone
                        // stops displaying a stale name.
                        onSaved={refreshCurrentView}
                    />
                )}
            </Dialog>

            <ConfirmBox
                open={openDeleteConfirm}
                title="Delete"
                content="Are you sure you want to delete this RTO City Location?"
                subContent="This action cannot be undone."
                img={DeleteImage}
                confirmButton="Delete"
                setOpenConfirmBox={setOpenDeleteConfirm}
                Function={confirmDelete}
                icon={<DeleteIcon />}
                color="error"
            />
        </>
    );
};

export default RTOCityLocation;
